import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, setStoredToken } from '../../services/apiClient';
import styles from './AuthPages.module.css';
import { brandConfig } from '../../config/brands';
import { useAuthBrandingSync, useAuthLogo, useAuthScheme } from '../../hooks/useAuthScheme';
import { appPath, termsHref, isExternalTerms } from '../../utils/navigation';

export default function EmailConfirmPage() {
  // ライトテーマ (usodeki 等) では認証カードを明色化する
  useAuthBrandingSync();
  const isLight = useAuthScheme() === 'light';
  const logo = useAuthLogo();
  const wrapperClass = `${styles.wrapper}${isLight ? ` ${styles.light}` : ''}`;
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [emailResent, setEmailResent] = useState(false);
  const [resentEmail, setResentEmail] = useState('');
  const [emailAlreadyConfirmed, setEmailAlreadyConfirmed] = useState(false);
  const [showResendForm, setShowResendForm] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  // メール再送信ハンドラ
  const handleResendConfirmation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resendEmail) return;

    setResendLoading(true);
    setError('');

    try {
      const response = await apiClient.post('/auth/email/resend-confirmation', {
        email: resendEmail.trim().toLowerCase(),
        origin: window.location.origin,
        partner_code: brandConfig.id,
      });

      // 既に確認済みの場合
      if (response.data.email_already_confirmed) {
        setEmailAlreadyConfirmed(true);
        setShowResendForm(false);
        return;
      }

      setEmailResent(true);
      setResentEmail(resendEmail);
      setShowResendForm(false);
    } catch (err: unknown) {
      const responseData = (err as { response?: { data?: { error?: string; email_already_confirmed?: boolean } } })?.response?.data;

      // エラーレスポンスでも既に確認済みの場合
      if (responseData?.email_already_confirmed) {
        setEmailAlreadyConfirmed(true);
        setShowResendForm(false);
        return;
      }

      setError(responseData?.error || '確認メールの送信に失敗しました');
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    const confirmEmail = async () => {
      // Supabase Auth はハッシュフラグメントでトークンを渡す
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash);

      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');

      // エラーパラメータをチェック（トークン期限切れ等）
      const errorCode = params.get('error');
      const errorDescription = params.get('error_description');
      const otpErrorCode = params.get('error_code');

      // アクセストークンがない場合
      if (!accessToken) {
        console.log('No access token found:', { errorCode, errorDescription, otpErrorCode });

        // otp_expired の場合は既に確認済みの可能性が高い → ログイン誘導
        if (otpErrorCode === 'otp_expired') {
          setEmailAlreadyConfirmed(true);
          setLoading(false);
          return;
        }

        // その他のエラーはフォームを表示
        setShowResendForm(true);
        setLoading(false);
        return;
      }

      // エラーがあってもトークンがあれば、バックエンドに送信してメール抽出を試みる
      // （期限切れトークンからメールを抽出して自動再送信）
      if (errorCode) {
        console.log('Token error detected, will attempt email extraction:', { errorCode, errorDescription });
      }

      try {
        const response = await apiClient.post('/auth/email/confirm', {
          access_token: accessToken,
          refresh_token: refreshToken || '',
          partner_code: brandConfig.id,
        });

        // メール再送信の場合
        if (response.data.email_resent) {
          setEmailResent(true);
          setResentEmail(response.data.email || '');
          setLoading(false);
          return;
        }

        // 既に確認済みの場合
        if (response.data.email_already_confirmed) {
          setEmailAlreadyConfirmed(true);
          setLoading(false);
          return;
        }

        const { token, redirect_to } = response.data;
        setStoredToken(token);

        // GTM dataLayer イベント送信（CV計測用）
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'email_confirmed'
        });

        if (redirect_to) {
          navigate(redirect_to, { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } catch (err: unknown) {
        const responseData = (err as { response?: { data?: { error?: string; email_resent?: boolean; email?: string; email_already_confirmed?: boolean; code?: string } } })?.response?.data;

        // エラーレスポンスでもメール再送信の場合がある
        if (responseData?.email_resent) {
          setEmailResent(true);
          setResentEmail(responseData.email || '');
          setLoading(false);
          return;
        }

        // エラーレスポンスでも既に確認済みの場合がある
        if (responseData?.email_already_confirmed) {
          setEmailAlreadyConfirmed(true);
          setLoading(false);
          return;
        }

        // トークンからメール抽出に失敗した場合はフォームを表示
        const errorCode = responseData?.code;
        if (errorCode === 'INVALID_TOKEN' || errorCode === 'RESEND_FAILED') {
          console.log('Could not extract email from token, showing resend form');
          setShowResendForm(true);
          setLoading(false);
          return;
        }

        setError(responseData?.error || 'メールの確認に失敗しました');
        setLoading(false);
      }
    };

    confirmEmail();
  }, [navigate]);

  if (loading) {
    return (
      <div className={wrapperClass}>
        <div className={styles.container}>
          <div className={styles.logoContainer}>
            <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
          </div>

          <div className={styles.card}>
            <div className={styles.successCard}>
              <h2>メールアドレスを確認中...</h2>
              <p>しばらくお待ちください</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 確認メール再送信成功
  if (emailResent) {
    return (
      <div className={wrapperClass}>
        <div className={styles.container}>
          <div className={styles.logoContainer}>
            <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
          </div>

          <div className={styles.card}>
            <div className={styles.successCard}>
              <h2>確認メールを再送信しました</h2>
              <p>
                {resentEmail ? `${resentEmail} に` : ''}新しい確認メールを送信しました。<br />
                メール内のリンクをクリックして、アカウントを有効化してください。
              </p>
              <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '16px' }}>
                メールが届かない場合は、迷惑メールフォルダもご確認ください。
              </p>
              <div className={styles.links} style={{ marginTop: '24px' }}>
                <a href={appPath('/')} className={styles.backButton}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                  ログインページへ戻る
                </a>
              </div>
            </div>
          </div>

          <footer className={styles.footer}>
            <div className={styles.footerLinks}>
              <a href={brandConfig.urls.privacy} target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>
              <span className={styles.footerDivider}>|</span>
              <a href={termsHref()} {...(isExternalTerms() ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>利用規約</a>
              <span className={styles.footerDivider}>|</span>
              <a href={brandConfig.urls.contact} target="_blank" rel="noopener noreferrer">お問い合わせ</a>
            </div>
          </footer>
        </div>
      </div>
    );
  }

  // 既にメール確認済み
  if (emailAlreadyConfirmed) {
    return (
      <div className={wrapperClass}>
        <div className={styles.container}>
          <div className={styles.logoContainer}>
            <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
          </div>

          <div className={styles.card}>
            <div className={styles.successCard}>
              <h2>メールアドレスは確認済みです</h2>
              <p>
                このメールアドレスは既に確認されています。<br />
                ログインしてサービスをご利用ください。
              </p>
              <div className={styles.links} style={{ marginTop: '24px' }}>
                <a href={appPath('/')} className={styles.backButton}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                  ログインページへ
                </a>
              </div>
            </div>
          </div>

          <footer className={styles.footer}>
            <div className={styles.footerLinks}>
              <a href={brandConfig.urls.privacy} target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>
              <span className={styles.footerDivider}>|</span>
              <a href={termsHref()} {...(isExternalTerms() ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>利用規約</a>
              <span className={styles.footerDivider}>|</span>
              <a href={brandConfig.urls.contact} target="_blank" rel="noopener noreferrer">お問い合わせ</a>
            </div>
          </footer>
        </div>
      </div>
    );
  }

  // 再送信フォーム表示
  if (showResendForm) {
    return (
      <div className={wrapperClass}>
        <div className={styles.container}>
          <div className={styles.logoContainer}>
            <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
          </div>

          <div className={styles.card}>
            <h1 className={styles.title}>確認リンクの有効期限切れ</h1>
            <p className={styles.subtitle}>
              このリンクは既に使用されたか、有効期限が切れています。<br />
              メールアドレスを入力して、新しい確認メールを受け取ってください。
            </p>

            {error && <div className={styles.error}>{error}</div>}

            <form className={styles.form} onSubmit={handleResendConfirmation}>
              <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="email">メールアドレス</label>
                <input
                  type="email"
                  id="email"
                  className={styles.input}
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="example@email.com"
                  required
                  autoComplete="email"
                />
              </div>

              <button type="submit" className={styles.submitButton} disabled={resendLoading}>
                {resendLoading ? '送信中...' : '確認メールを再送信'}
              </button>
            </form>

            <div className={styles.links}>
              <a href={appPath('/')} className={styles.link}>ログインページへ戻る</a>
            </div>
          </div>

          <footer className={styles.footer}>
            <div className={styles.footerLinks}>
              <a href={brandConfig.urls.privacy} target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>
              <span className={styles.footerDivider}>|</span>
              <a href={termsHref()} {...(isExternalTerms() ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>利用規約</a>
              <span className={styles.footerDivider}>|</span>
              <a href={brandConfig.urls.contact} target="_blank" rel="noopener noreferrer">お問い合わせ</a>
            </div>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <div className={styles.container}>
        <div className={styles.logoContainer}>
          <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
        </div>

        <div className={styles.card}>
          {error && (
            <div className={styles.successCard}>
              <h2>確認に失敗しました</h2>
              <div className={styles.error}>{error}</div>
              <div className={styles.links} style={{ marginTop: '24px' }}>
                <a href={appPath('/')} className={styles.backButton}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                  ログインページへ戻る
                </a>
              </div>
            </div>
          )}
        </div>

        <footer className={styles.footer}>
          <div className={styles.footerLinks}>
            <a href={brandConfig.urls.privacy} target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>
            <span className={styles.footerDivider}>|</span>
            <a href={termsHref()} {...(isExternalTerms() ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>利用規約</a>
          </div>
        </footer>
      </div>
    </div>
  );
}
